import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore, type AuthUser } from '../stores/auth.store';

// IXAM: in dev (Vite) `VITE_API_URL` non è settata → baseURL relativa `/api`,
// che il proxy Vite forwarda al backend HTTP locale. In prod il build gira
// con VITE_API_URL=https://api.ixam.tribusinformatica.it così che il FE
// chiami direttamente il backend sul subdomain api.
const apiOrigin = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const API_BASE = apiOrigin ? `${apiOrigin}/api` : '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        // IXAM: bypass our interceptor to avoid recursion. Fresh axios call,
        // cookie sent thanks to withCredentials. Usa la stessa baseURL del
        // resto dell'app (ergo /api in dev, https://api.ixam... in prod).
        const res = await axios.post<{ accessToken: string; user: AuthUser }>(
          `${API_BASE}/auth/refresh`,
          null,
          { withCredentials: true },
        );
        useAuthStore.getState().setAuth(res.data.accessToken, res.data.user);
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    const refreshable =
      status === 401 &&
      original &&
      !original._retry &&
      !url.startsWith('/auth/refresh') &&
      !url.startsWith('/auth/login');

    if (refreshable) {
      original!._retry = true;
      const newToken = await attemptRefresh();
      if (newToken) {
        original!.headers.Authorization = `Bearer ${newToken}`;
        return apiClient.request(original!);
      }
      useAuthStore.getState().clearAuth();
    }
    return Promise.reject(error);
  },
);
